/// <reference types="vite/client" />
import { UPDATE_READY_EVENT } from './install';

/**
 * Register public/sw.js, in production only.
 *
 * ── WHY NOT IN DEVELOPMENT ──
 * Vite serves the source on :5173 with nothing under /assets/ and an
 * index.html that changes on every save; a worker caching that would hand
 * you yesterday's page and a very confusing afternoon. `import.meta.env.PROD`
 * is false there and true in `vite build`, which is what the API serves.
 *
 * ── HOW A NEW BUILD REACHES A RUNNING PAGE ──
 * The browser re-fetches sw.js on navigation (and here, whenever the tab
 * comes back into view, which matters for an installed app that stays open
 * for days). A byte-different file - the build id changed - installs as a new
 * worker; it calls skipWaiting, so `installed` is followed by `activating`
 * without waiting for tabs to close. The page it is replacing is told through
 * the window event, and UpdateBar offers the reload that switches to it. The
 * FIRST install has no controller yet and announces nothing: there is no
 * "new version" of a page that was just loaded.
 *
 * A failed registration is swallowed. Installing is a nicety; the app is the
 * same app in a tab.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;

  const announce = () => window.dispatchEvent(new Event(UPDATE_READY_EVENT));

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing;
          if (!worker) return;
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) announce();
          });
        });
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void registration.update().catch(() => undefined);
        });
      })
      .catch(() => {
        // Not a secure context, or the file failed to parse. A tab still works.
      });
  });
}
